import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiMail, FiPhone, FiMapPin, FiEye, FiClock, FiArrowUpRight } from 'react-icons/fi';
import LocalizedLink from '../i18n/LocalizedLink';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import FooterSocialDock from './FooterSocialDock';
import { fetchFooter } from '../../features/footer/footerSlice';
import articleService from '../../features/articles/articleService';
import resolveImageUrl from '../../utils/resolveImageUrl';
import { resolveBrandSettings } from '../../features/branding/brandSettings';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.footer);
  const { settings: siteSettings } = useSelector((state) => state.siteSettings);
  const brand = resolveBrandSettings(useSelector((state) => state.branding.settings));
  const currentYear = new Date().getFullYear();
  const [showcaseArticles, setShowcaseArticles] = useState([]);

  const locale = useMemo(() => {
    const raw = String(i18n?.language || 'en').toLowerCase();
    if (raw.startsWith('zh')) return 'zh';
    if (raw.startsWith('sw')) return 'sw';
    return 'en';
  }, [i18n?.language]);

  useEffect(() => {
    dispatch(fetchFooter());
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;

    const pickShowcase = (items) => {
      const source = Array.isArray(items) ? items.filter(Boolean) : [];
      if (!source.length) return [];

      const byLatest = [...source].sort(
        (a, b) => new Date(b.publishedAt || b.createdAt || 0).getTime() - new Date(a.publishedAt || a.createdAt || 0).getTime()
      );
      const byViewsHigh = [...source].sort((a, b) => (Number(b.viewCount || 0) - Number(a.viewCount || 0)));
      const byViewsLow = [...source].sort((a, b) => (Number(a.viewCount || 0) - Number(b.viewCount || 0)));

      const selected = [];
      const pushUnique = (item) => {
        if (!item) return;
        const id = String(item._id || item.id || item.localizedSlug?.en || item.slug || '');
        if (!id) return;
        if (selected.some((it) => String(it._id || it.id || it.localizedSlug?.en || it.slug || '') === id)) return;
        selected.push(item);
      };

      pushUnique(byViewsHigh[0]);
      pushUnique(byLatest[0]);
      pushUnique(byViewsLow[0]);
      for (const article of byLatest) {
        if (selected.length >= 3) break;
        pushUnique(article);
      }
      return selected.slice(0, 3);
    };

    (async () => {
      try {
        const response = await articleService.listPublicArticles({ locale, page: 1, limit: 24 });
        if (!mounted) return;
        setShowcaseArticles(pickShowcase(response?.articles || []));
      } catch (_) {
        if (!mounted) return;
        setShowcaseArticles([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [locale]);

  const {
    tagline,
    quickLinks = [],
    serviceLinks = [],
    contactEmail: footerContactEmail,
    contactPhone: footerContactPhone,
    contactAddress: footerContactAddress,
    copyrightName,
    socialLinks = [],
  } = settings || {};
  const contactEmail = siteSettings?.supportEmail || footerContactEmail;
  const contactPhone = siteSettings?.supportPhone || footerContactPhone;
  const contactAddress = siteSettings?.contactAddress || footerContactAddress;
  const footerLogoUrl = resolveImageUrl(brand.logo.footer.url || brand.logo.primary.url);

  return (
    <footer className="bg-primary-950 text-pearl-100">
      <div className="container-shell section">
        {showcaseArticles.length > 0 && (
          <div className="mb-12 rounded-3xl border border-gold-400/30 bg-gradient-to-br from-[#1f1b18] via-[#151311] to-[#11100e] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.42)]">
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-400/90">
                  Editorial Showcase
                </p>
                <h2 className="mt-1 font-serif text-2xl text-pearl-100">
                  Travel Stories Worth Reading
                </h2>
              </div>
              <LocalizedLink to="/blog" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gold-300 hover:text-gold-200">
                All Articles <FiArrowUpRight className="h-3.5 w-3.5" />
              </LocalizedLink>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {showcaseArticles.map((article) => {
                const slug = article.localizedSlug?.[locale] || article.localizedSlug?.en || article.localizedSlug || article.slug;
                const title = article.title?.[locale] || article.title?.en || article.title || 'Travel Article';
                const excerpt = article.excerpt?.[locale] || article.excerpt?.en || article.excerpt || '';
                const image = resolveImageUrl(article.featuredImage || '');
                const dateValue = article.publishedAt || article.createdAt;
                return (
                  <LocalizedLink
                    key={String(article._id || slug || title)}
                    to={slug ? `/blog/${slug}` : '/blog'}
                    className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-0.5 hover:border-gold-300/45 hover:bg-white/[0.05]"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-black/25">
                      {image ? (
                        <img src={image} alt={title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900 text-xs uppercase tracking-[0.16em] text-pearl-400">
                          Pearl Voyages
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h2 className="line-clamp-2 font-serif text-lg leading-tight text-pearl-100">
                        {title}
                      </h2>
                      {excerpt ? <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-pearl-300/85">{excerpt}</p> : null}
                      <div className="mt-3 flex items-center gap-3 text-xs text-pearl-400">
                        {dateValue ? (
                          <span className="inline-flex items-center gap-1">
                            <FiClock className="h-3 w-3" />
                            {new Date(dateValue).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <FiEye className="h-3 w-3" />
                          {Number(article.viewCount || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </LocalizedLink>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Top Grid */}
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">

          {/* Brand */}
          <div>
            <img
              src={footerLogoUrl}
              alt={brand.logo.altText}
              className="h-14 w-auto md:h-[60px] lg:h-[72px]"
            />

            <p className="mt-5 text-sm text-pearl-400 leading-relaxed">
              {tagline ||
                t(
                  'footer.tagline',
                  'PearlBridge delivers curated travel and refined event experiences across destinations with precision, elegance, and cultural depth.'
                )}
            </p>

            <FooterSocialDock socialLinks={socialLinks} />

            <div className="mt-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-pearl-500">
                {t('footer.language', 'Language')}
              </p>
              <LanguageSwitcher variant="dark" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg text-gold-400 mb-5">
              {t('footer.quickLinks', 'Quick Links')}
            </h3>

            <ul className="space-y-3 text-sm">
              {(quickLinks.length > 0
                ? quickLinks
                : [
                    { href: '/', label: t('nav.home', 'Home') },
                    { href: '/mvp/packages', label: t('nav.packages', 'Packages') },
                    { href: '/mvp/destinations', label: t('nav.destinations', 'Destinations') },
                    { href: '/about', label: t('nav.about', 'About') },
                    { href: '/contact', label: t('nav.contact', 'Contact') },
                  ]
              ).map((link) => (
                <li key={link.href + link.label}>
                  <LocalizedLink
                    to={link.href}
                    className="text-pearl-300 hover:text-gold-400 transition"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-serif text-lg text-gold-400 mb-5">
              {t('footer.customerService', 'Services')}
            </h3>

            <ul className="space-y-3 text-sm">
              {(serviceLinks.length > 0
                ? serviceLinks
                : [
                    { href: '/mvp/inquiry', label: t('footer.sendInquiry', 'Send Inquiry') },
                    { href: '/blog', label: t('footer.travelInsights', 'Travel Insights') },
                  ]
              ).map((link) => (
                <li key={link.href + link.label}>
                  <LocalizedLink
                    to={link.href}
                    className="text-pearl-300 hover:text-gold-400 transition"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-lg text-gold-400 mb-5">
              {t('footer.contactUs', 'Contact')}
            </h3>

            <ul className="space-y-4 text-sm">
              {contactEmail && (
                <li className="flex items-start gap-3">
                  <FiMail className="mt-1 text-gold-400 shrink-0" />
                  <span className="text-pearl-300">{contactEmail}</span>
                </li>
              )}

              {contactPhone && (
                <li className="flex items-start gap-3">
                  <FiPhone className="mt-1 text-gold-400 shrink-0" />
                  <span className="text-pearl-300">{contactPhone}</span>
                </li>
              )}

              {contactAddress && (
                <li className="flex items-start gap-3">
                  <FiMapPin className="mt-1 text-gold-400 shrink-0" />
                  <span className="text-pearl-300 leading-relaxed">
                    {contactAddress}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="divider-gold h-px my-10" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between text-sm gap-3">
          <p className="text-pearl-400">
            &copy; {currentYear}{' '}
            <span className="text-pearl-200 font-medium">
              {copyrightName || brand.brandName}
            </span>
            . {t('footer.allRights', 'All rights reserved.')}
          </p>

          <p className="text-pearl-500">
            Developed by{' '}
            <a
              href="https://www.trophydevelopers.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-400 hover:underline"
            >
              Trophy Developers
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
